import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Target, X, Plus, Minus, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import FileUpload from './FileUpload';

const ChallengeProgressModal = ({ 
  challenge, 
  onClose, 
  onSubmit, 
  fetchProgress, 
  updateProgress 
}) => {
  const { t } = useTranslation();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressForm, setProgressForm] = useState({
    progress_notes: '',
    document_url: '',
    completion_proof: null
  });
  const [manualProgress, setManualProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadProgressData();
  }, [challenge.id]);

  const loadProgressData = async () => {
    setLoading(true);
    try {
      const data = await fetchProgress(challenge.id);
      setProgressData(data);
      setManualProgress(data?.progress || 0);
      setProgressForm(prev => ({
        ...prev,
        progress_notes: data?.participation?.progress_notes || ''
      }));
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProgressUpdate = async (newProgress) => {
    try {
      await updateProgress(challenge.id, {
        progress: newProgress,
        notes: progressForm.progress_notes
      });
      setManualProgress(newProgress);
      
      // Refresh data
      await loadProgressData();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleSubmitCompletion = async () => {
    if (!progressForm.completion_proof) {
      alert(t('challenges.proofRequired'));
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(challenge.id, {
        progress_notes: progressForm.progress_notes,
        document_url: progressForm.document_url
      });
      onClose();
    } catch (error) {
      // Error handling is done in the parent function
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'pending_review':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending_review':
        return <Clock className="w-4 h-4" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-amber-600" />
            {t('challenges.viewProgress')}: {challenge.title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Challenge Info & Progress */}
          <div className="lg:col-span-2 space-y-6">
            {/* Challenge Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('challenges.description')}</h3>
              <p className="text-gray-700 mb-3">{challenge.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>{t('challenges.pointsReward')}: <strong>{challenge.points_reward}</strong></span>
                {challenge.deadline && (
                  <span>{t('challenges.deadline')}: <strong>{new Date(challenge.deadline).toLocaleDateString()}</strong></span>
                )}
              </div>
            </div>

            {/* Current Progress */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{t('challenges.yourProgress')}</h3>
                {progressData?.status && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(progressData.status)}`}>
                    {getStatusIcon(progressData.status)}
                    {progressData.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500" 
                  style={{width: `${progressData?.progress || 0}%`}}
                ></div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {t('challenges.complete', { percent: progressData?.progress || 0 })}
                </span>
                {progressData?.status !== 'completed' && progressData?.status !== 'pending_review' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleProgressUpdate(Math.max(0, manualProgress - 10))}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-medium">{manualProgress}%</span>
                    <button
                      onClick={() => handleProgressUpdate(Math.min(100, manualProgress + 10))}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submission History */}
            {progressData?.submission && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Submission Status</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    <strong>Status:</strong> {progressData.submission.status.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Submitted:</strong> {new Date(progressData.submission.submitted_at).toLocaleString()}
                  </p>
                  {progressData.submission.progress_notes && (
                    <p className="text-sm text-gray-700">
                      <strong>Notes:</strong> {progressData.submission.progress_notes}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Completion Form */}
          <div className="space-y-6">
            {progressData?.status !== 'completed' && progressData?.status !== 'pending_review' ? (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">{t('challenges.markComplete')}</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('challenges.progressNotes')}
                    </label>
                    <textarea
                      value={progressForm.progress_notes}
                      onChange={(e) => setProgressForm({...progressForm, progress_notes: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder={t('challenges.progressPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('challenges.proofRequired')}
                    </label>
                    <FileUpload
                      onFileSelect={(file, url) => {
                        setProgressForm(prev => ({
                          ...prev,
                          completion_proof: file,
                          document_url: url
                        }));
                      }}
                      acceptedTypes=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      maxSizeMB={10}
                      placeholder={t('upload.dragDrop')}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t('challenges.acceptedFormats')}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleSubmitCompletion}
                      disabled={!progressForm.completion_proof || submitting}
                      className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {t('common.loading')}
                        </>
                      ) : (
                        t('challenges.submitReview')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-600" />
                <h3 className="font-semibold text-gray-900 mb-2">Challenge Submitted</h3>
                <p className="text-sm text-gray-600">
                  Your challenge completion has been submitted for review.
                </p>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeProgressModal;